import { Input as AntInput, Tooltip } from 'antd';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputProps } from 'antd/es/input';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { useRef, useState } from 'react';
import InfoCircleIcon from '../Icons/InfoCircleIcon';
import HelpInfo from './HelpInfo';
import { useHover } from 'hooks';
import validateInput from '../../../utils/validation';

interface Props extends InputProps {
  control: any;
  inputName: string;
  placeholder?: string;
  defaultValue?: string | number;
  error?: any;
  password?: boolean;
  email?: boolean;
  callback?: () => void;
  helpInfo?: string[];
  inputAccept?: 'all' | 'eng' | 'jp' | 'jp-katakana' | 'number' | 'alphabet';
  customClassName?: string;
}

export default function Input({
  control,
  inputName,
  placeholder = '',
  defaultValue = '',
  error,
  customClassName = '',
  password = false,
  email = false,
  callback = () => {},
  helpInfo = [],
  inputAccept = 'all',
  ...props
}: Props) {
  const { t } = useTranslation();
  const hoverRef = useRef(null);
  const passwordInput = useRef<any>();
  const isHover = useHover(hoverRef);
  const [passwordVisible, setPasswordVisible] = useState<boolean>();
  let type: string;

  if (password) {
    type = 'password';
  } else if (email) {
    type = 'email';
  } else {
    type = 'text';
  }
  return (
    <Controller
      control={control}
      name={inputName}
      // rules={rules}
      defaultValue={defaultValue}
      render={({ field: { onChange, value, name } }) => (
        <div
          className={`custom-input ${customClassName} ${
            props?.hidden ? 'hidden' : ''
          }`}
        >
          {type !== 'password' ? (
            <AntInput
              value={value}
              onChange={v => {
                if (validateInput(v.target.value, inputAccept)) {
                  onChange(v.target.value.trimStart());
                  callback();
                } else {
                  if (value === undefined) {
                    onChange('');
                  }
                }
              }}
              className={error?.message ? 'error-input' : ''}
              placeholder={placeholder}
              name={name}
              type={type}
              autoComplete={'new-password'}
              suffix={
                <Tooltip
                  title={
                    error?.message ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t(error?.message),
                        }}
                      />
                    ) : (
                      <HelpInfo helpInfo={helpInfo} />
                    )
                  }
                  overlayClassName={
                    error?.message ? 'error-tooltip' : 'help-tooltip'
                  }
                  placement="topRight"
                  color={''}
                  arrowPointAtCenter
                  trigger={
                    window.matchMedia('(any-pointer: coarse)').matches
                      ? 'click'
                      : 'hover'
                  }
                >
                  <div style={{ display: 'flex' }} ref={hoverRef}>
                    {error?.message ? (
                      <InfoCircleIcon />
                    ) : helpInfo.length > 0 ? (
                      <InfoCircleIcon type="help" isHover={isHover} />
                    ) : null}
                  </div>
                </Tooltip>
              }
              {...props}
            />
          ) : (
            <AntInput
              ref={passwordInput}
              value={value}
              onChange={v => {
                onChange(v.target.value.trimStart());
                callback();
              }}
              className={error?.message ? 'error-input' : ''}
              placeholder={placeholder}
              name={name}
              type={passwordVisible && !props.disabled ? 'text' : 'password'}
              autoComplete={'new-password'}
              suffix={
                <>
                  {passwordVisible && !props.disabled ? (
                    <EyeOutlined
                      onClick={() => {
                        passwordInput.current.blur();
                        setPasswordVisible(!passwordVisible);
                      }}
                      width="20"
                      height="20"
                      style={
                        !props.disabled
                          ? { marginRight: '5px' }
                          : { marginRight: '5px', cursor: 'not-allowed' }
                      }
                    />
                  ) : (
                    <EyeInvisibleOutlined
                      onClick={() => {
                        passwordInput.current.blur();
                        setPasswordVisible(!passwordVisible);
                      }}
                      width="20"
                      height="20"
                      style={
                        !props.disabled
                          ? { marginRight: '5px' }
                          : { marginRight: '5px', cursor: 'not-allowed' }
                      }
                    />
                  )}
                  <Tooltip
                    title={
                      t(error?.message) || <HelpInfo helpInfo={helpInfo} />
                    }
                    overlayClassName={
                      error?.message ? 'error-tooltip' : 'help-tooltip'
                    }
                    placement="topRight"
                    color={''}
                    arrowPointAtCenter
                    trigger={
                      window.matchMedia('(any-pointer: coarse)').matches
                        ? 'click'
                        : 'hover'
                    }
                  >
                    <div style={{ display: 'flex' }} ref={hoverRef}>
                      {error?.message ? (
                        <InfoCircleIcon />
                      ) : helpInfo.length > 0 ? (
                        <InfoCircleIcon type="help" isHover={isHover} />
                      ) : null}
                    </div>
                  </Tooltip>
                </>
              }
              {...props}
            />
          )}
        </div>
      )}
    />
  );
}
